import type { LucideIcon } from "@/components/icons";
import type { ReactNode } from "react";

import type { ControlDensity } from "@/lib/design-system/control-density";

export interface InlineComboboxOption {
  value: string;
  label: string;
  sublabel?: string;
  description?: string;
  keywords?: string[];
  disabled?: boolean;
  trailing?: ReactNode;
}

export interface InlineComboboxGroup {
  heading: string;
  options: readonly InlineComboboxOption[];
}

function normalize(value: string): string {
  return value.toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ").trim();
}

export function filterInlineGroups(
  groups: InlineComboboxGroup[],
  query: string,
): InlineComboboxGroup[] {
  const needle = normalize(query);
  if (!needle) return groups;
  return groups
    .map((group) => ({
      ...group,
      options: group.options.filter((option) =>
        normalize(
          [option.label, option.sublabel, option.description]
            .filter(Boolean)
            .concat(option.keywords ?? [])
            .join(" "),
        ).includes(needle),
      ),
    }))
    .filter((group) => group.options.length > 0);
}

export interface InlineComboboxProps {
  options?: readonly InlineComboboxOption[];
  groups?: InlineComboboxGroup[];
  value: string | null;
  placeholder: string;
  searchPlaceholder?: string;
  displayValue?: string;
  onSelect: (value: string) => void;
  onClear?: () => void;
  onEdit?: () => void;
  onEditOption?: (value: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  minSearchLength?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  emptyText?: string;
  heading?: string;
  notice?: string;
  action?: { label: string; onSelect: () => void };
  footer?: (close: () => void) => ReactNode;
  optionIcon?: LucideIcon;
  leadingIcon?: ReactNode;
  renderOption?: (option: InlineComboboxOption, isSelected: boolean) => ReactNode;
  renderValue?: () => ReactNode;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  ariaDescribedby?: string;
  ariaRequired?: boolean;
  id?: string;
  disabled?: boolean;
  size?: ControlDensity;
  variant?: "field" | "filled" | "ghost" | "value" | "card";
  className?: string;
  popoverClassName?: string;
}

export interface InlineComboboxListProps {
  listId: string;
  groups: InlineComboboxGroup[];
  value: string | null;
  activeValue: string | null;
  isLoading: boolean;
  emptyText: string;
  minSearchMessage: string | null;
  optionIcon?: LucideIcon;
  renderOption?: (option: InlineComboboxOption, isSelected: boolean) => ReactNode;
  onEditOption?: (value: string) => void;
  editAsText?: boolean;
  onChoose: (option: InlineComboboxOption) => void;
  onHighlight: (value: string) => void;
}
